/**
 * Test page panel (epic 100 task 04, D7) — the Custom Opt-in test link.
 * Enter a test email, create the link, copy it or open it: the landing route
 * renders the campaign's page exactly as a visitor sees it. The address is
 * marked as a test — nothing the page does is counted or shown to visitors.
 * Posts `{ mode: 'page', testEmail }`; the created run's `launchUrl` is the
 * capability link (task 02). Error `code`s map to plain sentences (D8).
 */

import { useState, type FormEvent } from 'react';
import type { CampaignDetailResponse } from '../../lib/api';
import type { TestRunSummary } from '@leads-platform/shared';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { CopyButton } from '../../components/ui/copy-button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { createRun, TestRunApiError } from './test-runs.api';

interface PageRunPanelProps {
  campaign: CampaignDetailResponse;
  /** Called with the created run — the screen refreshes the list and selects it. */
  onCreated: (run: TestRunSummary) => void;
}

/** Create-error sentences by the body's machine `code` (D8). */
const CODE_SENTENCES: Record<string, string> = {
  test_lead_exists: 'A lead with this email already exists on this campaign — use another email.',
  campaign_archived: 'This campaign is archived — unarchive it to test it.',
  test_run_not_applicable:
    "This campaign's send settings can't be tested yet — check the Advertiser Send section.",
  landing_base_url_missing: "Test pages aren't configured on this server — tell the developers.",
  test_page_secret_missing: "Test pages aren't configured on this server — tell the developers.",
};

function sentenceFor(err: unknown): string {
  if (err instanceof TestRunApiError && err.code && CODE_SENTENCES[err.code]) {
    return CODE_SENTENCES[err.code];
  }
  return err instanceof Error && err.message ? err.message : 'Creating the test link failed.';
}

export function PageRunPanel({ campaign, onCreated }: PageRunPanelProps) {
  const [testEmail, setTestEmail] = useState('');
  const [created, setCreated] = useState<TestRunSummary | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const email = testEmail.trim();
    if (!email) return;
    setSubmitting(true);
    setError(null);
    try {
      const run = await createRun(campaign.id, { mode: 'page', testEmail: email });
      setCreated(run);
      onCreated(run);
    } catch (err) {
      setCreated(null);
      setError(sentenceFor(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Test page</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="max-w-2xl text-sm text-muted">
          Opens this page as a visitor sees it, whatever the campaign&apos;s status. Nothing is
          counted or shown to visitors. Click-out: the button opens the advertiser&apos;s real URL
          with this email, so they receive it. Lead-post: Accept sends a real test lead to the
          advertiser and, if a Click destination URL is set, also opens it with this email.
        </p>
        <form onSubmit={handleSubmit} className="max-w-xl space-y-4">
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="space-y-2">
            <Label htmlFor="page-test-email">Test email</Label>
            <Input
              id="page-test-email"
              type="email"
              required
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </div>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Creating…' : 'Create test link'}
          </Button>
        </form>

        {created?.launchUrl && (
          <div className="max-w-xl space-y-3 rounded-lg border border-line bg-paper/60 p-4">
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-faint">
                Test link
              </div>
              <div className="flex items-start gap-2">
                <span className="min-w-0 flex-1 break-all font-mono text-xs text-ink">
                  {created.launchUrl}
                </span>
                <CopyButton value={created.launchUrl} label="Copy" title="Copy the test link" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                onClick={() =>
                  created.launchUrl &&
                  window.open(created.launchUrl, '_blank', 'noopener,noreferrer')
                }
              >
                Open in new tab
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
